import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import useMediaQuery from "@mui/material/useMediaQuery";

import Captions from "core/components/Captions";
import VideoPreview from "core/components/VideoPreview";
import VideoLoading from "core/components/VideoLoading";
import PlayerPause from "core/components/PlayerPause";
import PlayerHeader from "core/components/PlayerHeader";
import PlayerFooter from "core/components/PlayerFooter";
import BigPlayButton from "core/components/BigPlayButton";
import Shortcut from "core/components/Shortcut";
import SeekIndicator from "core/components/SeekIndicator";
import VolumeIndicator from "core/components/VolumeIndicator";
import { createEvent } from "core/utils/tracker";
import useIdleRef from "core/hooks/useIdleRef";
import ViewerIdWatermark from "core/components/ViewerIdWatermark";
import useVideoPostMessages from "core/hooks/useVideoPostMessages";
import useVideoStatePostMessage from "core/hooks/useVideoStatePostMessage";

// Интервал показа мидролов (в секундах) - 15 мин
const MIDROLL_IMPRESSION_INTERVAL = 60 * 15;

import SponsorPlayerLogo from "core/components/SponsorPlayerLogo";
import SponsorPlayerButton from "core/components/SponsorPlayerButton";
import SponsorMidAdv from "core/components/SponsorMidAdv";
import SponsorPauseAdv from "core/components/SponsorPauseAdv";
import SponsorPostAdv from "core/components/SponsorPostAdv";
import SponsorLiveAdv from "core/components/SponsorLiveAdv";
import PlayerInView from "core/components/PlayerInView";
import VideoAnnotationsOverlay from "core/components/VideoAnnotationsOverlay";

export default function VideoScreenContent({
  closeRouteDialogButton,
  controls = true,
  videoElement,
  videoState,
  videoActions,
  relatedContent,
  pauseRecommendations = true,
  shareOnMobile,
  chooseCameraButton,
  handleSeekEnd,
  handleSeek,
  handleSeekStart,
  seekValue,
  isSeeking,
  inStreamBannerHeight,
  setInStreamBannerHeight,
  cornerBannerHeight,
  setCornerBannerHeight,
  videoRef,
  sponsorCornerBannerPlace,
  sponsorPlayerLogoPlace,
  sponsorPlayerButtonPlace,
  sponsorInStreamBannerPlace,
  sponsorVideoPlace,
  autoPause,
  fullscreenTarget,
  autoPlay,
  content,
  muted,
  pause,
  hideTitle,
  loop,
  hideShare,
  hideLogo,
  disableTitleLink,
  video,
  start,
  setPause,
  finish,
  nextPlaylistContentButton,
  onVideoError,
  triggerPostMessage,
}) {
  const idle = useIdleRef({ ref: videoRef });
  const visible = !idle;

  const [seekAmount, setSeekAmount] = useState(0);
  const [showVolumeIndicator, setShowVolumeIndicator] = useState(false);
  const volumeTimeout = useRef(null);

  // Keyboard seeking state
  const [isKeyboardSeeking, setIsKeyboardSeeking] = useState(false);

  // Рекомендации, открытые кнопкой в панели плеера, а не самой паузой.
  const [relatedOpen, setRelatedOpen] = useState(false);

  // Блок похожих видео сам скрывается в маленьком плеере (RelatedVideo:
  // до 600px по ширине или 375px по высоте). Там кнопка открыла бы пустой
  // экран с одним названием, поэтому показываем её, только если блок влезет.
  const relatedFits = useMediaQuery(
    "(min-width: 601px) and (min-height: 376px)"
  );

  // Видео могут продолжить в обход блока рекомендаций: пробелом,
  // командой play от встраивающей страницы или автопаузой при прокрутке.
  // Тогда блок закрываем, иначе он остался бы поверх играющего видео.
  // Закрываем только на переходе «не играло → заиграло»: в момент нажатия
  // кнопки видео ещё играет, пауза от плеера приходит чуть позже.
  const prevStatusRef = useRef(videoState?.status);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = videoState?.status;

    if (prev !== "playing" && videoState?.status === "playing") {
      setRelatedOpen(false);
    }
  }, [videoState?.status]);
  const [keyboardSeekValue, setKeyboardSeekValue] = useState(0);

  useVideoPostMessages({
    videoActions,
    contentId: content?.id,
  });
  useVideoStatePostMessage({
    videoState,
    triggerPostMessage,
    contentId: content?.id,
  });

  // Очистка таймаута при unmount
  useEffect(() => {
    return () => {
      if (volumeTimeout.current) {
        clearTimeout(volumeTimeout.current);
      }
    };
  }, []);

  const handleSeekChange = (amount) => {
    setSeekAmount(amount);
  };

  const handleVolumeChange = () => {
    // Очищаем предыдущий таймаут если он существует
    if (volumeTimeout.current) {
      clearTimeout(volumeTimeout.current);
    }

    setShowVolumeIndicator(true);
    volumeTimeout.current = setTimeout(() => {
      setShowVolumeIndicator(false);
      volumeTimeout.current = null;
    }, 1000); // Держим show=true дольше, чтобы индикатор успел показать все изменения при удержании клавиши
  };

  // Keyboard seeking callbacks
  const handleKeyboardSeekStart = (time) => {
    setIsKeyboardSeeking(true);
    setKeyboardSeekValue(time);
  };

  const handleKeyboardSeekChange = (time) => {
    setKeyboardSeekValue(time);
  };

  const handleKeyboardSeekEnd = () => {
    setIsKeyboardSeeking(false);
  };

  const onClickBigPlay = () => {
    videoActions.togglePlayUser();
    if (videoState.status === "playing") {
      createEvent("video", "pause");
    } else {
      createEvent("video", "play");
    }
  };

  const onClickPlay = () => {
    videoActions.togglePlayUser();
    createEvent("video", "play");
  };

  const onClickPause = () => {
    videoActions.togglePlayUser();
    createEvent("video", "pause");
  };

  const onFinishPostroll = () => {
    if (loop) {
      videoActions.play();
    }
  };

  const onChangeInView = (inView) => {
    if (autoPause) {
      if (inView) {
        if (!videoState.isUserPause) {
          videoActions.play();
        }
      } else {
        videoActions.pause();
      }
    }
  };

  let playerHeaderVisible = false;
  let playerFooterVisible = false;

  if (isSeeking || isKeyboardSeeking) {
    playerFooterVisible = true;
  }

  // if (!cornerBannerHeight && !inStreamBannerHeight) {
  if (pause) {
    playerHeaderVisible = true;
  } else {
    if (hideTitle) {
      if (visible || videoState.isUserPause) {
        playerHeaderVisible = true;
        playerFooterVisible = true;
      }
    } else {
      if (videoState.isUserPause) {
        if (visible) {
          playerHeaderVisible = true;
          playerFooterVisible = true;
        }
      } else {
        if (visible) {
          playerHeaderVisible = true;
          playerFooterVisible = true;
        }
      }
    }
  }
  // }

  const sponsorPlayerLogoVisible = true;
  // !(
  //   cornerBannerHeight > 0 || inStreamBannerHeight > 0
  // );

  const sponsorPlayerButtonVisible = true;
  // !(
  //   cornerBannerHeight > 0 || inStreamBannerHeight > 0
  // );

  const btn =
    nextPlaylistContentButton &&
    nextPlaylistContentButton({ videoState, visible: playerFooterVisible });

  return (
    <>
      <PlayerInView
        cornerBannerHeight={cornerBannerHeight}
        inStreamBannerHeight={inStreamBannerHeight}
        onChange={onChangeInView}
      >
        <ViewerIdWatermark
          onVideoError={onVideoError}
          watermarked={content.enableViewerIdWatermark}
          videoRef={videoRef}
        >
          {videoElement}
        </ViewerIdWatermark>

        <VideoAnnotationsOverlay
          timecodes={content?.contentTimecodes?.timecodes || []}
          currentTime={videoState?.currentTime || 0}
          paused={videoState?.paused || false}
        />

        <VideoPreview
          visible={isSeeking}
          videoPreviews={video.videoPreviews}
          seekValue={seekValue}
        />

        <VideoLoading videoState={videoState} />

        <Shortcut
          videoActions={videoActions}
          videoState={videoState}
          onSeekChange={handleSeekChange}
          onVolumeChange={handleVolumeChange}
          onKeyboardSeekStart={handleKeyboardSeekStart}
          onKeyboardSeekChange={handleKeyboardSeekChange}
          onKeyboardSeekEnd={handleKeyboardSeekEnd}
        />

        <SeekIndicator seekAmount={seekAmount} />

        <VolumeIndicator
          volume={videoState?.volume || 0}
          muted={videoState?.muted || false}
          show={showVolumeIndicator}
        />

        {!hideTitle &&
        controls &&
        (videoState.isEnded ||
          relatedOpen ||
          (pauseRecommendations && videoState.isUserPause)) ? (
          <PlayerPause
            title={content.title}
            contentUrl={content.shareUrl}
            relatedContent={relatedContent}
            onClickPlay={() => {
              if (relatedOpen) {
                // Блок открыт кнопкой «Похожие видео»: видео стоит на
                // паузе, и клик должен его продолжить, а не переключить.
                setRelatedOpen(false);
                videoActions.play();
                return;
              }

              onClickBigPlay();
            }}
            setVisible={relatedOpen ? undefined : setPause}
            visible={relatedOpen || pause}
            hideLogo={hideLogo || content.hideLogo}
            disableTitleLink={disableTitleLink}
          />
        ) : (
          <>
            {sponsorPlayerLogoVisible &&
              sponsorPlayerButtonVisible &&
              playerFooterVisible && (
                <BigPlayButton
                  onClick={onClickBigPlay}
                  videoState={videoState}
                />
              )}
          </>
        )}

        {controls && (
          <PlayerHeader
            content={content}
            position="absolute"
            visible={playerHeaderVisible}
            tracker
            closeRouteDialogButton={closeRouteDialogButton}
          />
        )}

        {controls && (
          <PlayerFooter
            position="absolute"
            content={content}
            shareOnMobile={shareOnMobile}
            onClickRelated={
              !pauseRecommendations &&
              relatedContent &&
              relatedFits &&
              !videoState.isEnded
                ? () => {
                    // Пауза именно пользовательская: иначе автопауза при
                    // прокрутке сама продолжит видео под блоком.
                    if (videoState.status === "playing") {
                      videoActions.togglePlayUser();
                    }
                    setRelatedOpen(true);
                  }
                : undefined
            }
            videoPreviews={video.videoPreviews}
            visible={playerFooterVisible}
            videoElement={videoElement}
            videoState={videoState}
            videoActions={videoActions}
            onClickPlay={onClickPlay}
            onClickPause={onClickPause}
            onSeekStart={handleSeekStart}
            onSeek={handleSeek}
            onSeekEnd={handleSeekEnd}
            hideShare={hideShare}
            hideLogo={hideLogo}
            fullscreenTarget={fullscreenTarget}
            disableSeekBarPopup={!hideLogo && videoState.isEnded && !loop}
            chooseCameraButton={chooseCameraButton}
            hlsUrl={video.playlist}
            start={start}
            finish={finish}
            isKeyboardSeeking={isKeyboardSeeking}
            keyboardSeekValue={keyboardSeekValue}
          />
        )}

        {btn}

        {content.isAdvEnabled && sponsorPlayerLogoPlace && controls && (
          <SponsorPlayerLogo
            contentId={content.id}
            rightholderId={content.rightholder.id}
            place={sponsorPlayerLogoPlace}
            footerVisible={playerFooterVisible}
            visible={sponsorPlayerLogoVisible}
          />
        )}

        {content.isAdvEnabled &&
          !content.widget &&
          sponsorPlayerButtonPlace &&
          controls && (
            <SponsorPlayerButton
              contentId={content.id}
              rightholderId={content.rightholder.id}
              place={sponsorPlayerButtonPlace}
              footerVisible={playerFooterVisible}
              visible={sponsorPlayerButtonVisible}
            />
          )}

        <Captions
          cues={videoState.activeCues}
          visible={visible || videoState.isUserPause}
        />
      </PlayerInView>

      {content.isAdvEnabled && (
        <>
          {sponsorVideoPlace && (
            <SponsorLiveAdv
              fullscreenTarget={fullscreenTarget}
              videoActions={videoActions}
              videoState={videoState}
              content={content}
              rightholderId={content.rightholder.id}
              sponsorVideoPlace={sponsorVideoPlace}
              muted={muted || autoPlay}
              impressionInterval={MIDROLL_IMPRESSION_INTERVAL}
              disabled={!sponsorPlayerLogoVisible || !sponsorPlayerLogoVisible}
            />
          )}

          {sponsorCornerBannerPlace && (
            <SponsorMidAdv
              duration={videoState.duration}
              fullscreenTarget={fullscreenTarget}
              videoActions={videoActions}
              videoState={videoState}
              contentId={content.id}
              rightholderId={content.rightholder.id}
              sponsorInStreamBannerPlace={sponsorInStreamBannerPlace}
              sponsorCornerBannerPlace={sponsorCornerBannerPlace}
              setInStreamBannerHeight={setInStreamBannerHeight}
              setCornerBannerHeight={setCornerBannerHeight}
            />
          )}

          {sponsorVideoPlace && (
            <SponsorPauseAdv
              fullscreenTarget={fullscreenTarget}
              videoState={videoState}
              content={content}
              rightholderId={content.rightholder.id}
              sponsorVideoPlace={sponsorVideoPlace}
              muted={muted}
            />
          )}

          {sponsorVideoPlace && (
            <SponsorPostAdv
              fullscreenTarget={fullscreenTarget}
              videoState={videoState}
              content={content}
              rightholderId={content.rightholder.id}
              sponsorVideoPlace={sponsorVideoPlace}
              muted={muted || autoPlay}
              onFinish={onFinishPostroll}
            />
          )}
        </>
      )}
    </>
  );
}

VideoScreenContent.propTypes = {
  pauseRecommendations: PropTypes.bool,
  shareOnMobile: PropTypes.bool,
  fullscreenTarget: PropTypes.string,
  muted: PropTypes.bool,
  loop: PropTypes.bool,
  hideTitle: PropTypes.bool,
  hideShare: PropTypes.bool,
  hideLogo: PropTypes.bool,
  disableTitleLink: PropTypes.bool,
  autoPlay: PropTypes.bool,
  autoPause: PropTypes.bool,
  controls: PropTypes.bool,
  triggerPostMessage: PropTypes.bool,
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shareUrl: PropTypes.string.isRequired,
    widget: PropTypes.object,
    enableViewerIdWatermark: PropTypes.bool,

    relatedContent: PropTypes.arrayOf(PropTypes.object),
    hideRelatedContent: PropTypes.bool,
    isAdvEnabled: PropTypes.bool,
    rightholder: PropTypes.shape({
      id: PropTypes.string,
      hideRelatedContent: PropTypes.bool,
    }),
    sport: PropTypes.shape({
      title: PropTypes.string,
      slug: PropTypes.string,
      shareUrl: PropTypes.string,
    }),
    contentTimecodes: PropTypes.shape({
      id: PropTypes.string,
      timecodes: PropTypes.arrayOf(PropTypes.object),
    }),
    hideLogo: PropTypes.bool,
  }),
  video: PropTypes.shape({
    id: PropTypes.string.isRequired,
    playlist: PropTypes.string.isRequired,
    duration: PropTypes.number,
    viewerVideoPosition: PropTypes.number,
    videoPreviews: PropTypes.object,
  }),

  start: PropTypes.number,
  finish: PropTypes.number,
  sponsorPlayerLogoPlace: PropTypes.string,
  sponsorPlayerButtonPlace: PropTypes.string,
  sponsorInStreamBannerPlace: PropTypes.string,
  sponsorCornerBannerPlace: PropTypes.string,
  sponsorVideoPlace: PropTypes.string,

  videoElement: PropTypes.object,
  videoState: PropTypes.object,
  videoActions: PropTypes.object,
  relatedContent: PropTypes.object,
  chooseCameraButton: PropTypes.object,
  handleSeekEnd: PropTypes.func,
  handleSeek: PropTypes.func,
  handleSeekStart: PropTypes.func,
  seekValue: PropTypes.number,
  isSeeking: PropTypes.bool,
  inStreamBannerHeight: PropTypes.object,
  setInStreamBannerHeight: PropTypes.func,
  cornerBannerHeight: PropTypes.object,
  setCornerBannerHeight: PropTypes.func,

  videoRef: PropTypes.object,
  pause: PropTypes.bool,
  setPause: PropTypes.func,

  nextPlaylistContentButton: PropTypes.func,
  onVideoError: PropTypes.func,
  closeRouteDialogButton: PropTypes.node,
};
